import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { API } from "@/siteConfig";
import { BookCallLink } from "@/components/BookCall";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="blog-post-not-found">
        <div className="text-center">
          <p className="font-serif text-3xl text-white">Article not found.</p>
          <Link to="/blog" className="btn-ghost mt-8" data-testid="blog-post-back-missing">
            <ArrowLeft className="h-4 w-4" /> Back to Insights
          </Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="blog-post-loading">
        <p className="font-sans text-sm text-white/40">Loading…</p>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page">
      <article className="mx-auto max-w-3xl px-6 pt-40 pb-24 md:pt-48 md:pb-32">
        <Reveal>
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="group inline-flex items-center gap-2 font-sans text-sm text-white/50 transition-colors duration-300 hover:text-teal"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            All Insights
          </Link>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="overline-label mt-12">{post.category}</p>
          <h1
            className="mt-6 font-serif text-3xl leading-tight tracking-tight text-white sm:text-4xl md:text-5xl"
            data-testid="blog-post-title"
          >
            {post.title}
          </h1>
          <p className="mt-6 font-sans text-xs uppercase tracking-[0.2em] text-white/40">
            {formatDate(post.date)} · {post.read_time}
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12 border-t border-white/10 pt-12" data-testid="blog-post-body">
            {post.body.map((block, i) =>
              block.startsWith("## ") ? (
                <h2 key={i} className="mt-10 mb-4 font-serif text-2xl text-teal">
                  {block.replace("## ", "")}
                </h2>
              ) : (
                <p key={i} className="mb-6 font-sans text-base leading-relaxed text-white/70 md:text-lg">
                  {block}
                </p>
              )
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border-t border-white/10 pt-10">
            <p className="font-serif text-xl italic text-white/70">
              Facing your own turning point?
            </p>
            <BookCallLink testid="blog-post-cta" className="btn-primary mt-6">
              Book a Call
              <ArrowUpRight className="h-4 w-4" />
            </BookCallLink>
          </div>
        </Reveal>
      </article>

      <CTASection
        testid="blog-post-bottom-cta"
        headline="Let's Talk About Your Turning Point"
        body="Every engagement starts with a conversation — no obligation, no canned pitch."
      />
    </main>
  );
}
